// src/editors/FunnelEditor/StepNode.js
import React, { useState, useRef, useLayoutEffect, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import ToolbarNode from './ToolbarNode';
import {BlockButton, BlockText, BlockImage, BlockLoading, BlockQuiz,    BlockPrice, BlockMetrics, BlockAlert}  from '../../../components/blocks/';
import { FaArrowRight } from 'react-icons/fa';
// Helper to get block heights and refs
function useBlockHeights(blocks) {
  
  // For each block, create a ref
  const refs = useRef([]);
  // Store heights after render
  const [heights, setHeights] = useState([]);

  useLayoutEffect(() => {
    if (!blocks || blocks.length === 0) return;
    
    // Use requestAnimationFrame to avoid ResizeObserver issues
    const updateHeights = () => {
      const newHeights = blocks.map((_, i) => {
        const el = refs.current[i];
        return el ? el.offsetHeight : 40; // fallback to 40
      });
      
      // Only update if heights actually changed
      setHeights(prevHeights => {
        const hasChanged = newHeights.some((h, i) => h !== prevHeights[i]);
        return hasChanged ? newHeights : prevHeights;
      });
    };

    const timeoutId = setTimeout(updateHeights, 0);
    return () => clearTimeout(timeoutId);
  }, [blocks]);

  return [refs, heights];
}

export default function StepNode({ data, selected, editingNodeId, selectedNodeId, isDarkMode }) {


  const [showRealBlocks, setShowRealBlocks] = useState(false);

  
  // Memoize flat blocks to prevent unnecessary recalculations
  const flatBlocks = useMemo(() => {
    const blocks = [];
    (data.blocks || []).forEach((block) => {
      if (block.type === 'quiz') {
        block.options.forEach((opt) => {
          blocks.push({ ...opt, type: 'option', parentBlockId: block.id });
        });
      } else {
        blocks.push({ ...block, type: block.type });
      }
    });
    return blocks;
  }, [data.blocks]);

  const [showToolbar, setShowToolbar] = useState(false);  
  
  const [blockRefs, blockHeights] = useBlockHeights(flatBlocks);

 

  // Memoize source handle positions to prevent recalculation on every render
  const sourceHandles = useMemo(() => {
    let y = 0;
    const positions = [];
    for (let i = 0; i < flatBlocks.length; i++) {
      const block = flatBlocks[i];
      const height = blockHeights[i] || 40; // fallback to 40 if not measured
      // Only buttons and options get handles
      if (block.type === 'button' || block.type === 'option' || block.type === 'loading') {
        positions.push({
          id: block.id,
          label: block.text || (block.type === 'button' ? 'Botão' : ''),
          top: y + height / 2,
        });
      }
      y += height + 8; // 8px = space-y-2
    }
    return positions;
  }, [flatBlocks, blockHeights]);

  // Stabilize the onClick handler

   
  const handleNodeClick = useCallback((data) => {
    
     
    if (selected) { 
     
      setShowToolbar(true);
    }
  }, [selected]);

    
  const nodeStyle = {
    backgroundColor: isDarkMode ? 'rgb(31, 41, 55)' : 'white',
    color: isDarkMode ? 'white' : 'black',
  }

  const isStartStep = data?.data?.id === 'start-step';

  if(isStartStep){
      nodeStyle.backgroundColor = 'rgb(248, 109, 29)';
      nodeStyle.color = 'white';
  }
 
 
  return (
    <div
      className={
        `relative border-2 rounded-xl p-4 w-[300px] shadow-md transition-all duration-150 ${
          isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'
        } ` +
        (selected
          ? "border-blue-500 shadow-blue-200 ring-2 ring-blue-200"
          : isDarkMode ? "border-gray-600" : "border-gray-200")
      }
      style={{
        boxShadow: selected
          ? "0 4px 24px 0rgba(168, 96, 2, 0.3)"
          : "0 1px 4px 0 #0001",
        backgroundColor: nodeStyle.backgroundColor,
        color: nodeStyle.color,
      }}
      onClick={handleNodeClick}
    >
      {selected && showToolbar && !isStartStep && (
        
        <ToolbarNode 
          data={data} 
          duplicateNode={data.duplicateNode} 
          removeNode={data.removeNode}  
          setEditingNodeId={data.setEditingNodeId} 
          selectedNodeId={selectedNodeId}
          selected={selected}   
          setConfigPanelOpen={data.setConfigPanelOpen}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Handle de entrada */}
      {!isStartStep && (
      <Handle
        type="target"
        position={Position.Left}
        id={data.id}
        
        style={{
          left: "-12px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "#a5b4fc",
          border: "2px solid #fff",
          width: 15,
          height: 15,
          borderRadius: "50%",
          zIndex: 10,
        }}
      />
      )}
      {/* Source handles, aligned to the center of each button/option */}

      {isStartStep && (
        <div
        key={data.data.id}
        style={{
          position: 'absolute',
          top: `${50}%`,
          right: '-12px',
          zIndex: 10,
        }}
      >
        <Handle
          type="source"
          position={Position.Right}
         
          style={{
            background: '#60a5fa',
            border: '2px solid #fff',
            width: 15,
            height: 15,
            borderRadius: '50%',
            zIndex: 10,
          }}
          title={`Conectar a primeira etapa`}
        />
      </div>
      )}

      {sourceHandles.map((h) => (
        <div
          key={h.id}
          style={{
            position: 'absolute',
            top: `${h.top + 54}px`,
            right: '-12px',
            zIndex: 10,
          }}
        >
          
          <Handle
            type="source"
            position={Position.Right}
            id={h.id}
            style={{
              background: '#60a5fa',
              border: '2px solid #fff',
              width: 20,
              height: 20,
              borderRadius: '50%',
              zIndex: 10,
              transform: 'translateX(-40px) translateY(-10px)', 
            }}
            title={`Conectar "${h.label}"`}
          />
        </div>
      ))}
      
      <div className="flex justify-between items-center mb-2">
        <div className="font-bold text-lg text-center flex-1">
          {data.title && data.title.length > 20
            ? data.title.slice(0, 20) + "…"
            : data.title}
        </div>
      </div>
      
      {/* Blocos, cada um com ref para medir altura */}
      <div className="space-y-2">
        {flatBlocks.map((block, i) => (
          <BlockPreview
            key={`${block.id}-${i}`} // More stable key
            block={block}
            showRealBlocks={showRealBlocks}
            ref={el => blockRefs.current[i] = el}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>


      <div className="absolute bottom-0 right-0">
        {data.data?.id != 'start-step' && (
        <Handle
            type="source"
            position={Position.Right}
            id={`handle-${data.id}-all`}
            style={{
              background: 'blue',
              border: '2px solid #fff',
              width: 20,
              height: 20,
              borderRadius: '50%',
              zIndex: 10,
            }}
            title={`Conectar Todos"`}
          />
        )}
      </div>
    </div>
  );
}

// Forward ref to measure block height
const BlockPreview = React.forwardRef(function BlockPreview({ block, showRealBlocks, isDarkMode }, ref) {
  // Memoize block content to prevent unnecessary re-renders
  

  if(showRealBlocks){

 
    if (block.type === 'text') {
      return (
         <BlockText block={block} />
      );
    }
    
    if (block.type === 'button') {
      return (
        <BlockButton block={block} />
      );
    }
    
 
    
    if (block.type === 'quiz') {
      // Should not happen in flatBlocks, but fallback
      return (
        <BlockQuiz block={block} />
      );
    }
    
    if (block.type === 'image') {
      return (
        <BlockImage block={block} />
      );
    }

    if (block.type === 'loading') {
      return (
        <BlockLoading block={block} />
      );
    }

    if (block.type === 'price') {
      return (
        <BlockPrice block={block} />
      );
    }

    if (block.type === 'metrics') {
      return (
        <BlockMetrics block={block} />
      );
    }

    if (block.type === 'alert') {
      return (
        <BlockAlert block={block} />
      );
    }
    if(block.type === 'quiz'){
      return (
        <BlockQuiz block={block} />
      );
    }
    // Outros tipos
    return (
      <div className={`flex items-center justify-center m-[0px] rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`} style={{ height: 40 }}>
      <span className={`text-center ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{block.type}</span>
    </div>
    );

  }else{
  
    let bgColor = isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
    let urlRedirect = '';
 
    
    if(block.target){
      bgColor = isDarkMode ? 'bg-blue-800' : 'bg-blue-100';
    }
    if(block.type === 'option' || block.type === 'button' || block.type === 'loading'){

 
        if(block.destinationType !== 'step'){
          bgColor = isDarkMode ? 'bg-green-800' : 'bg-green-200';
          urlRedirect = block.url;
        }else{
          bgColor = isDarkMode ? 'bg-blue-700' : 'bg-blue-200';
        }
    
    }
    block.label = block.type;

    if(block.type === 'option'){
      if(block.content.length > 30){
        block.label = block.content.replace(/<[^>]+>/g, ' ').slice(0, 30) + '...';
      }else{
        block.label = block.content.replace(/<[^>]+>/g, ' ');
      }
    }
    if(block.type === 'button'){
      block.label = <span dangerouslySetInnerHTML={{ __html: block.label }} />  ;
    }
    return (
      <div>
      <div className={`flex items-center justify-center ${bgColor} m-[0px] rounded`} style={{ height: 40 }}>
        <span className={`text-center ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>


         {block.label}

      
          </span>

          {block.destinationType !== 'step' && urlRedirect && (
            <div className={`absolute ${isDarkMode ? 'bg-green-600' : 'bg-green-500'} left-0 rounded-lg p-2 flex items-center justify-center`} style={{transform: 'translateX(310px)'}} >
              <span className="text-center text-white  flex items-center justify-center">  
                 <FaArrowRight />
                <span className="text-[18px] font-semibold pl-1">{block.destinationType !== 'step' ? urlRedirect : ''}</span>

              </span>
            </div>
          )}

      



        </div>
        </div>
        
    );
  }
 });